import { useState } from 'react';
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Text,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import SectionCard from '../../components/SectionCard';
import ValidatedDateField from '../../components/ValidatedDateField';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import type {
  InsuranceHistoryCardProps,
  PropertyInsuranceHistoryData,
} from './types';

const InsuranceHistoryCard = ({
  insuranceHistory,
  onInsuranceHistoryChange,
  isUpdating,
}: InsuranceHistoryCardProps) => {
  const { answerCodes } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();
  const [isLastCancellationOutOfRange, setIsLastCancellationOutOfRange] =
    useState(false);

  const policyData = { insurance_history: insuranceHistory };
  const isRequired = (field: string) =>
    isFieldRequired('policy', field, policyData as Record<string, unknown>);

  const currentInsurerOptions =
    answerCodes?.hab_current_auto_insurer?.map((o) => ({
      value: o.code,
      label: o.label,
    })) || [];

  const propertyInsurerOptions =
    answerCodes?.hab_current_habitational_insurer?.map((o) => ({
      value: o.code,
      label: o.label,
    })) || [];

  const handleChange = (
    field: keyof PropertyInsuranceHistoryData,
    value: any,
  ) => {
    onInsuranceHistoryChange({ [field]: value });
  };

  const handleDateChange = (field: keyof PropertyInsuranceHistoryData) => {
    return (date: Date | null) => {
      if (date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        handleChange(field, `${year}-${month}-${day}`);
      } else {
        handleChange(field, '');
      }
    };
  };

  const parseDate = (dateStr?: string): Date | null => {
    if (!dateStr) return null;
    return new Date(dateStr + 'T00:00:00');
  };

  const handleCancelledChange = (value: string) => {
    if (value === 'yes') {
      handleChange('cancelled_for_non_payment', true);
    } else {
      onInsuranceHistoryChange({
        cancelled_for_non_payment: false,
        num_cancellations: null,
        date_of_last_cancellation: null,
        date_of_cancellation_end: null,
      });
    }
  };

  const handleFraudChange = (value: string) => {
    if (value === 'yes') {
      handleChange('cancelled_for_fraud', true);
    } else {
      onInsuranceHistoryChange({
        cancelled_for_fraud: false,
        date_of_fraud_cancellation: null,
        date_of_fraud_cancellation_end: null,
      });
    }
  };

  const handleMisrepresentationChange = (value: string) => {
    if (value === 'yes') {
      handleChange('cancelled_for_misrepresentation', true);
    } else {
      onInsuranceHistoryChange({
        cancelled_for_misrepresentation: false,
        date_of_misrepresentation_cancellation: null,
        date_of_misrepresentation_cancellation_end: null,
      });
    }
  };

  const isCancelled = insuranceHistory.cancelled_for_non_payment === true;
  const isFraudCancelled = insuranceHistory.cancelled_for_fraud === true;
  const isMisrepresentationCancelled =
    insuranceHistory.cancelled_for_misrepresentation === true;

  const isMultiPolicyEnabled = insuranceHistory.multi_policy_discount === true;
  const hasPriorInsurance = insuranceHistory.has_prior_insurance === true;
  const isPriorInsuranceFieldsEnabled = hasPriorInsurance;

  const handlePriorInsuranceChange = (value: string) => {
    if (value === 'yes') {
      handleChange('has_prior_insurance', true);
    } else {
      onInsuranceHistoryChange({
        has_prior_insurance: false,
        continuously_insured_since: null,
        year_policy_first_held_with_broker: null,
        property_current_insured_since: null,
        current_habitational_insurer: '',
        prior_policy_number: '',
        auto_current_insured_since: null,
        current_auto_insurer: '',
        multi_property_discount: false,
        multi_policy_discount: false,
        apply_for_all_carriers: true,
      });
    }
  };

  return (
    <SectionCard mb={4}>
      <Text fontSize="md" fontWeight="700" mb={4}>
        Insurance History
      </Text>

      <Box mb={4}>
        <FormControl
          isRequired={isRequired('insurance_history.has_prior_insurance')}
          isInvalid={
            isRequired('insurance_history.has_prior_insurance') &&
            insuranceHistory.has_prior_insurance === undefined
          }
        >
          <FormLabel fontSize="sm" fontWeight="600" mb={1}>
            Previously Insured?
          </FormLabel>
          <RadioGroup
            value={
              insuranceHistory.has_prior_insurance === true
                ? 'yes'
                : insuranceHistory.has_prior_insurance === false
                  ? 'no'
                  : ''
            }
            onChange={handlePriorInsuranceChange}
            isDisabled={isUpdating}
          >
            <HStack spacing={4}>
              <Radio value="yes" colorScheme="blue">
                Yes
              </Radio>
              <Radio value="no" colorScheme="blue">
                No
              </Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
      </Box>

      <Flex gap={4} mb={4} wrap="wrap">
        <Box minW="160px" flex="1" maxW="200px">
          <ValidatedDateField
            itemPath="policy"
            fieldPath="insurance_history.continuously_insured_since"
            label="Insured Since"
            date={parseDate(insuranceHistory.continuously_insured_since)}
            entity={policyData as Record<string, unknown>}
            isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
            onChange={handleDateChange('continuously_insured_since')}
          />
        </Box>

        <Box minW="160px" flex="1" maxW="220px">
          <DateField
            label="Insured with Brokerage Since"
            date={parseDate(
              insuranceHistory.year_policy_first_held_with_broker,
            )}
            isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
            onChange={handleDateChange('year_policy_first_held_with_broker')}
          />
        </Box>

        <Box minW="160px" flex="1" maxW="240px">
          <ValidatedDateField
            itemPath="policy"
            fieldPath="insurance_history.property_current_insured_since"
            label="Property - Current Insured Since"
            date={parseDate(insuranceHistory.property_current_insured_since)}
            entity={policyData as Record<string, unknown>}
            isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
            onChange={handleDateChange('property_current_insured_since')}
          />
        </Box>

        <Box flex="2" minW="200px" maxW="300px">
          <FormControl
            isRequired={isRequired(
              'insurance_history.current_habitational_insurer',
            )}
            isInvalid={
              isRequired('insurance_history.current_habitational_insurer') &&
              !insuranceHistory.current_habitational_insurer
            }
          >
            <FormLabel
              fontSize="sm"
              fontWeight="600"
              mb={1}
              color={!isPriorInsuranceFieldsEnabled ? 'gray.400' : undefined}
            >
              Property - Current Insurer
            </FormLabel>
            <SelectSearch
              options={propertyInsurerOptions}
              value={insuranceHistory.current_habitational_insurer || ''}
              onChange={(val) =>
                handleChange(
                  'current_habitational_insurer',
                  val === null ? '' : String(val),
                )
              }
              placeholder="Select insurer..."
              isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
              isInvalid={
                isRequired('insurance_history.current_habitational_insurer') &&
                !insuranceHistory.current_habitational_insurer
              }
              allowClear
            />
          </FormControl>
        </Box>

        <Box minW="120px" maxW="150px">
          <FormControl>
            <FormLabel
              fontSize="sm"
              fontWeight="600"
              mb={1}
              color={!isPriorInsuranceFieldsEnabled ? 'gray.400' : undefined}
            >
              Prior Policy #
            </FormLabel>
            <Input
              variant="main"
              value={insuranceHistory.prior_policy_number || ''}
              onChange={(e) =>
                handleChange('prior_policy_number', e.target.value)
              }
              isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
              placeholder=""
            />
          </FormControl>
        </Box>
      </Flex>

      <Flex gap={4} mb={4} wrap="wrap" align="flex-end">
        <Box minW="160px" maxW="200px">
          <DateField
            label="Auto - Current Insured Since"
            date={parseDate(insuranceHistory.auto_current_insured_since)}
            isDisabled={
              isUpdating ||
              !isMultiPolicyEnabled ||
              !isPriorInsuranceFieldsEnabled
            }
            required={
              isMultiPolicyEnabled &&
              isPriorInsuranceFieldsEnabled &&
              isRequired('insurance_history.auto_current_insured_since')
            }
            onChange={handleDateChange('auto_current_insured_since')}
            bg={
              !isMultiPolicyEnabled || !isPriorInsuranceFieldsEnabled
                ? 'gray.50'
                : undefined
            }
          />
        </Box>

        <Box flex="1" minW="200px" maxW="300px">
          <FormControl
            isRequired={
              isMultiPolicyEnabled &&
              isPriorInsuranceFieldsEnabled &&
              isRequired('insurance_history.current_auto_insurer')
            }
            isInvalid={
              isMultiPolicyEnabled &&
              isPriorInsuranceFieldsEnabled &&
              isRequired('insurance_history.current_auto_insurer') &&
              !insuranceHistory.current_auto_insurer
            }
          >
            <FormLabel
              fontSize="sm"
              fontWeight="600"
              mb={1}
              color={
                !isMultiPolicyEnabled || !isPriorInsuranceFieldsEnabled
                  ? 'gray.400'
                  : undefined
              }
            >
              Auto - Current Insurer
            </FormLabel>
            <SelectSearch
              options={currentInsurerOptions}
              value={insuranceHistory.current_auto_insurer || ''}
              onChange={(val) =>
                handleChange(
                  'current_auto_insurer',
                  val === null ? '' : String(val),
                )
              }
              placeholder="Select insurer..."
              isDisabled={
                isUpdating ||
                !isMultiPolicyEnabled ||
                !isPriorInsuranceFieldsEnabled
              }
              isInvalid={
                isMultiPolicyEnabled &&
                isPriorInsuranceFieldsEnabled &&
                isRequired('insurance_history.current_auto_insurer') &&
                !insuranceHistory.current_auto_insurer
              }
              allowClear
            />
          </FormControl>
        </Box>

        <HStack spacing={6} pb={2} align="flex-start">
          <Checkbox
            isChecked={insuranceHistory.multi_property_discount || false}
            onChange={(e) =>
              handleChange('multi_property_discount', e.target.checked)
            }
            colorScheme="blue"
            isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
          >
            <Text
              fontSize="sm"
              fontWeight="500"
              color={!isPriorInsuranceFieldsEnabled ? 'gray.400' : undefined}
            >
              Multi-Property
            </Text>
          </Checkbox>

          <Checkbox
            isChecked={insuranceHistory.multi_policy_discount || false}
            onChange={(e) =>
              handleChange('multi_policy_discount', e.target.checked)
            }
            colorScheme="blue"
            isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
          >
            <Text
              fontSize="sm"
              fontWeight="500"
              color={!isPriorInsuranceFieldsEnabled ? 'gray.400' : undefined}
            >
              Multi-Line Policy
            </Text>
          </Checkbox>

          <Checkbox
            isChecked={insuranceHistory.apply_for_all_carriers ?? true}
            onChange={(e) =>
              handleChange('apply_for_all_carriers', e.target.checked)
            }
            colorScheme="blue"
            isDisabled={isUpdating || !isPriorInsuranceFieldsEnabled}
          >
            <Text
              fontSize="sm"
              fontWeight="500"
              color={!isPriorInsuranceFieldsEnabled ? 'gray.400' : undefined}
            >
              Apply for All Carriers
            </Text>
          </Checkbox>
        </HStack>
      </Flex>

      <Flex gap={4} align="flex-end" wrap="wrap" mb={4}>
        <FormControl maxW="fit-content">
          <FormLabel fontSize="sm" fontWeight="600" mb={1}>
            Has the client been cancelled for non-payment within the past 6
            years?
          </FormLabel>
          <RadioGroup
            value={
              insuranceHistory.cancelled_for_non_payment === true
                ? 'yes'
                : insuranceHistory.cancelled_for_non_payment === false
                  ? 'no'
                  : ''
            }
            onChange={handleCancelledChange}
            isDisabled={isUpdating}
          >
            <HStack spacing={4}>
              <Radio value="yes" colorScheme="blue">
                Yes
              </Radio>
              <Radio value="no" colorScheme="blue">
                No
              </Radio>
            </HStack>
          </RadioGroup>
        </FormControl>

        <FormControl
          maxW="140px"
          isRequired={
            isCancelled && isRequired('insurance_history.num_cancellations')
          }
          isInvalid={
            isCancelled &&
            isRequired('insurance_history.num_cancellations') &&
            insuranceHistory.num_cancellations === null
          }
        >
          <FormLabel
            fontSize="sm"
            fontWeight="600"
            mb={1}
            whiteSpace="nowrap"
            color={!isCancelled ? 'gray.400' : undefined}
          >
            # Of Cancellations
          </FormLabel>
          <NumberInput
            variant="main"
            value={insuranceHistory.num_cancellations ?? ''}
            onChange={(_, value) =>
              handleChange('num_cancellations', isNaN(value) ? null : value)
            }
            min={0}
            precision={0}
            isDisabled={isUpdating || !isCancelled}
            isInvalid={
              isCancelled &&
              isRequired('insurance_history.num_cancellations') &&
              insuranceHistory.num_cancellations === null
            }
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>

        <Box minW="180px">
          <DateField
            label="Date of Last Cancellation"
            date={parseDate(insuranceHistory.date_of_last_cancellation)}
            isDisabled={isUpdating || !isCancelled}
            required={
              isCancelled &&
              isRequired('insurance_history.date_of_last_cancellation')
            }
            maxDate={
              parseDate(insuranceHistory.date_of_cancellation_end) ?? undefined
            }
            isInvalid={isLastCancellationOutOfRange}
            onOutOfRange={setIsLastCancellationOutOfRange}
            onChange={handleDateChange('date_of_last_cancellation')}
          />
        </Box>

        <Box minW="180px">
          <ValidatedDateField
            itemPath="policy"
            fieldPath="insurance_history.date_of_cancellation_end"
            label="Date of Last Coverage Reinstated"
            date={parseDate(insuranceHistory.date_of_cancellation_end)}
            entity={policyData as Record<string, unknown>}
            isDisabled={isUpdating || !isCancelled}
            minDate={
              parseDate(insuranceHistory.date_of_last_cancellation) ?? undefined
            }
            onChange={handleDateChange('date_of_cancellation_end')}
          />
        </Box>
      </Flex>

      <Flex gap={4} align="flex-end" wrap="wrap" mb={4}>
        <FormControl maxW="fit-content">
          <FormLabel fontSize="sm" fontWeight="600" mb={1}>
            Has the client been cancelled for Fraud?
          </FormLabel>
          <RadioGroup
            value={
              insuranceHistory.cancelled_for_fraud === true
                ? 'yes'
                : insuranceHistory.cancelled_for_fraud === false
                  ? 'no'
                  : ''
            }
            onChange={handleFraudChange}
            isDisabled={isUpdating}
          >
            <HStack spacing={4}>
              <Radio value="yes" colorScheme="blue">
                Yes
              </Radio>
              <Radio value="no" colorScheme="blue">
                No
              </Radio>
            </HStack>
          </RadioGroup>
        </FormControl>

        {isFraudCancelled && (
          <>
            <Box minW="180px">
              <ValidatedDateField
                itemPath="policy"
                fieldPath="insurance_history.date_of_fraud_cancellation"
                label="Date of Fraud Cancellation"
                date={parseDate(insuranceHistory.date_of_fraud_cancellation)}
                entity={policyData as Record<string, unknown>}
                isDisabled={isUpdating}
                maxDate={
                  parseDate(insuranceHistory.date_of_fraud_cancellation_end) ??
                  undefined
                }
                onChange={handleDateChange('date_of_fraud_cancellation')}
              />
            </Box>

            <Box minW="180px">
              <ValidatedDateField
                itemPath="policy"
                fieldPath="insurance_history.date_of_fraud_cancellation_end"
                label="Date Coverage Reinstated"
                date={parseDate(
                  insuranceHistory.date_of_fraud_cancellation_end,
                )}
                entity={policyData as Record<string, unknown>}
                isDisabled={isUpdating}
                minDate={
                  parseDate(insuranceHistory.date_of_fraud_cancellation) ??
                  undefined
                }
                onChange={handleDateChange('date_of_fraud_cancellation_end')}
              />
            </Box>
          </>
        )}
      </Flex>

      <Flex gap={4} align="flex-end" wrap="wrap" mb={4}>
        <FormControl maxW="fit-content">
          <FormLabel fontSize="sm" fontWeight="600" mb={1}>
            Has the client been cancelled for Misrepresentation?
          </FormLabel>
          <RadioGroup
            value={
              insuranceHistory.cancelled_for_misrepresentation === true
                ? 'yes'
                : 'no'
            }
            onChange={handleMisrepresentationChange}
            isDisabled={isUpdating}
          >
            <HStack spacing={4}>
              <Radio value="yes" colorScheme="blue">
                Yes
              </Radio>
              <Radio value="no" colorScheme="blue">
                No
              </Radio>
            </HStack>
          </RadioGroup>
        </FormControl>

        {isMisrepresentationCancelled && (
          <>
            <Box minW="180px">
              <ValidatedDateField
                itemPath="policy"
                fieldPath="insurance_history.date_of_misrepresentation_cancellation"
                label="Date of Misrepresentation Cancellation"
                date={parseDate(
                  insuranceHistory.date_of_misrepresentation_cancellation,
                )}
                entity={policyData as Record<string, unknown>}
                isDisabled={isUpdating}
                maxDate={
                  parseDate(
                    insuranceHistory.date_of_misrepresentation_cancellation_end,
                  ) ?? undefined
                }
                onChange={handleDateChange(
                  'date_of_misrepresentation_cancellation',
                )}
              />
            </Box>

            <Box minW="180px">
              <ValidatedDateField
                itemPath="policy"
                fieldPath="insurance_history.date_of_misrepresentation_cancellation_end"
                label="Date Coverage Reinstated"
                date={parseDate(
                  insuranceHistory.date_of_misrepresentation_cancellation_end,
                )}
                entity={policyData as Record<string, unknown>}
                isDisabled={isUpdating}
                minDate={
                  parseDate(
                    insuranceHistory.date_of_misrepresentation_cancellation,
                  ) ?? undefined
                }
                onChange={handleDateChange(
                  'date_of_misrepresentation_cancellation_end',
                )}
              />
            </Box>
          </>
        )}
      </Flex>
    </SectionCard>
  );
};

export default InsuranceHistoryCard;
